<template>
  <div class="informe-uso-container">
    <CardPrime class="informe-uso-card">
      <template #title>
        <div class="card-header">
          <h3 class="card-title">Informe de Interacción de Juegos - Múltiples Cursos y Asignaturas</h3>
        </div>
      </template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="form-field">
            <label for="colegio-select" class="block text-sm font-medium text-gray-700">Seleccionar Colegio:</label>
            <ColegioDropdown
              v-model="selectedColegio"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona un Colegio'"
              :error="errors.colegio"
              :options="colegios"
              optionLabel="nombre"
              optionValue="id_colegio"
              placeholder="Selecciona un colegio"
              @update:modelValue="cargarCursosYAsignaturas"
            />
            <small v-if="mostrarErrores && errors.colegio" class="p-error">{{ errors.colegio }}</small>
          </div>

          <div class="form-field">
            <label for="cursos-select" class="block text-sm font-medium text-gray-700">Seleccionar Cursos:</label>
            <CursoDropdown
              v-model="selectedCursos"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona uno o más cursos'"
              :error="errors.cursos"
              :options="cursos"
              :multiple="true"
              optionLabel="nombre"
              optionValue="id_curso"
              placeholder="Selecciona uno o más cursos"
              :disabled="!selectedColegio"
              @update:modelValue="onCursoChange"
            />
            <small v-if="mostrarErrores && errors.cursos" class="p-error">{{ errors.cursos }}</small>
          </div>

          <div class="form-field">
            <label for="asignaturas-select" class="block text-sm font-medium text-gray-700">Seleccionar Asignaturas:</label>
            <AsignaturaDropdown
              v-model="selectedAsignaturas"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona una o más asignaturas'"
              :error="errors.asignaturas"
              :options="asignaturas"
              :multiple="true"
              optionLabel="glosa"
              optionValue="id_asignatura"
              placeholder="Selecciona una o más asignaturas"
              :disabled="!selectedColegio"
              @update:modelValue="onAsignaturaChange"
            />
            <small v-if="mostrarErrores && errors.asignaturas" class="p-error">{{ errors.asignaturas }}</small>
          </div>

          <div class="form-field">
            <label for="inicio" class="block text-sm font-medium text-gray-700">Fecha Inicio:</label>
            <input
              type="date"
              id="inicio"
              v-model="inicio"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          <div class="form-field">
            <label for="fin" class="block text-sm font-medium text-gray-700">Fecha Fin:</label>
            <input
              type="date"
              id="fin"
              v-model="fin"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>

        <ButtonPrime
          label="Generar Informe"
          @click="generarInformeJuegos"
          class="mb-4"
          :disabled="loading"
        />

        <div v-if="resumenJuegos.length > 0" class="mt-4">
          <div class="flex justify-end mb-2">
            <ButtonPrime
              label="Exportar a Excel"
              icon="pi pi-file-excel"
              class="p-button-success"
              @click="exportToExcel"
            />
          </div>
          <h4 class="text-lg font-semibold mb-2">Resumen por Asignatura:</h4>
          <DataTable :value="resumenJuegos" class="p-datatable-sm" dataKey="id_asignatura">
            <ColumnPrime field="nombre_asignatura" header="Asignatura" sortable />
            <ColumnPrime field="correctas" header="Correctas" sortable />
            <ColumnPrime field="incorrectas" header="Incorrectas" sortable />
          </DataTable>
        </div>

        <div v-if="informeJuegos.length > 0" class="mt-4">
          <h4 class="text-lg font-semibold mb-2">Detalle de Interacciones de Juegos:</h4>
          <DataTable
            :value="informeJuegos"
            :paginator="true"
            :rows="rows"
            :totalRecords="totalRecordsJuegos"
            :lazy="true"
            :loading="loading"
            @page="onPageJuegos"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            :rowsPerPageOptions="[10, 20, 50]"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            dataKey="id_juego"
          >
            <ColumnPrime field="id_juego" header="ID Juego" sortable />
            <ColumnPrime field="id_alumno" header="ID Alumno" sortable />
            <ColumnPrime field="rut_alumno" header="RUT Alumno" sortable />
            <ColumnPrime field="asignatura" header="Asignatura" sortable />
            <ColumnPrime field="curso" header="Curso" sortable />
            <ColumnPrime field="colegio" header="Colegio" sortable />
            <ColumnPrime field="nombre_alumno" header="Nombre Alumno" sortable />
            <ColumnPrime field="capitulo" header="Capítulo" sortable />
            <ColumnPrime field="video" header="Video" sortable />
            <ColumnPrime field="glosa_pregunta" header="Pregunta" sortable />
            <ColumnPrime field="respuesta_juego" header="Respuesta" sortable />
            <ColumnPrime field="fecha_inicio_juego" header="Fecha Inicio" sortable>
              <template #body="slotProps">
                {{ new Date(slotProps.data.fecha_inicio_juego).toLocaleString('es-CL') }}
              </template>
            </ColumnPrime>
            <ColumnPrime field="fecha_fin_juego" header="Fecha Fin" sortable>
              <template #body="slotProps">
                {{ new Date(slotProps.data.fecha_fin_juego).toLocaleString('es-CL') }}
              </template>
            </ColumnPrime>
            <ColumnPrime field="puntaje_obtenido" header="Puntaje Obtenido" sortable />
          </DataTable>
        </div>
        <div
          v-else-if="informeJuegos.length === 0 && !loading && wasGeneratedJuegos"
          class="mt-4 p-4 bg-yellow-100 text-yellow-700 rounded"
        >
          No se encontraron datos para los filtros seleccionados.
        </div>

        <ToastPrime />
      </template>
    </CardPrime>
  </div>
</template>

<script>
import usuarioService from '@/services/UsuarioService';
import CardPrime from 'primevue/card';
import ButtonPrime from 'primevue/button';
import DataTable from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import ToastPrime from 'primevue/toast';
import CursoDropdown from '@/components/selectores/CursoDropdown.vue';
import AsignaturaDropdown from '@/components/selectores/AsignaturaDropdown.vue';
import ColegioDropdown from '@/components/selectores/ColegioDropdown.vue';
import * as XLSX from 'xlsx';

export default {
  components: {
    CardPrime,
    ButtonPrime,
    DataTable,
    ColumnPrime,
    ToastPrime,
    CursoDropdown,
    AsignaturaDropdown,
    ColegioDropdown,
  },
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      selectedColegio: '',
      selectedCursos: [],
      selectedAsignaturas: [],
      colegios: [],
      cursos: [],
      asignaturas: [],
      inicio: firstDay.toISOString().split('T')[0],
      fin: lastDay.toISOString().split('T')[0],
      informeJuegos: [],
      resumenJuegos: [],
      totalRecordsJuegos: 0,
      rows: 10,
      currentPageJuegos: 1,
      loading: false,
      wasGeneratedJuegos: false,
      errors: {
        colegio: '',
        cursos: '',
        asignaturas: '',
      },
      mostrarErrores: false,
    };
  },
  methods: {
    async loadColegios() {
      try {
        this.loading = true;
        const response = await usuarioService.getColegios();
        this.colegios = response.data || [];
        if (this.colegios.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron colegios.',
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error al cargar colegios:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudieron cargar los colegios.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async cargarCursosYAsignaturas() {
      this.selectedCursos = [];
      this.selectedAsignaturas = [];
      this.cursos = [];
      this.asignaturas = [];

      if (!this.selectedColegio) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Por favor, selecciona un colegio.',
          life: 3000,
        });
        return;
      }

      this.loading = true;
      try {
        const cursosResponse = await usuarioService.getCursosPorColegio(this.selectedColegio);
        this.cursos = cursosResponse.data || [];
        if (this.cursos.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron cursos para el colegio seleccionado.',
            life: 3000,
          });
        }

        const asignaturasResponse = await usuarioService.getAsignaturasPorColegio(this.selectedColegio);
        this.asignaturas = asignaturasResponse.data || [];
        if (this.asignaturas.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron asignaturas para el colegio seleccionado.',
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error al cargar cursos o asignaturas:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudieron cargar los cursos o asignaturas.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async generarInformeJuegos() {
      this.mostrarErrores = false;
      this.wasGeneratedJuegos = true;

      if (!this.selectedColegio) {
        this.errors.colegio = 'Debes seleccionar un colegio';
        this.mostrarErrores = true;
      }
      if (this.selectedCursos.length === 0) {
        this.errors.cursos = 'Debes seleccionar al menos un curso';
        this.mostrarErrores = true;
      }
      if (!this.inicio || !this.fin || new Date(this.inicio) > new Date(this.fin)) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Fechas inválidas o incompletas.',
          life: 3000,
        });
        return;
      }
      if (this.mostrarErrores) return;

      this.loading = true;
      try {
        const response = await usuarioService.getInformeInteraccionJuegoMultiple(
          this.inicio,
          this.fin,
          this.selectedColegio,
          this.selectedCursos,
          this.selectedAsignaturas.length > 0 ? this.selectedAsignaturas : null,
          this.currentPageJuegos,
          this.rows
        );

        if (
          !response ||
          !response.Detalle ||
          !Array.isArray(response.Detalle) ||
          !response.Resumen ||
          !Array.isArray(response.Resumen)
        ) {
          throw new Error('Respuesta inválida del servidor: datos no disponibles.');
        }

        this.informeJuegos = response.Detalle.map((item) => ({
          ...item,
          nombre_completo: `${item.nombre_alumno}`,
        }));
        this.resumenJuegos = response.Resumen;
        this.totalRecordsJuegos = response.TotalRecords || this.informeJuegos.length;
        this.$toast.add({
          severity: 'success',
          summary: 'Éxito',
          detail: response.Mensaje || 'Informe de interacciones de juegos generado con éxito.',
          life: 3000,
        });
      } catch (error) {
        console.error('Error al generar informe de juegos:', error);
        this.informeJuegos = [];
        this.resumenJuegos = [];
        this.totalRecordsJuegos = 0;
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo generar el informe.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    exportToExcel() {
      if (!this.resumenJuegos.length) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'No hay datos para exportar.',
          life: 3000,
        });
        return;
      }

      const colegio = this.colegios.find((c) => c.id_colegio === this.selectedColegio)?.nombre || 'No especificado';
      const cursos = this.selectedCursos
        .map((id) => this.cursos.find((c) => c.id_curso === id)?.nombre || id)
        .join(', ');
      const asignaturas = this.selectedAsignaturas.length > 0
        ? this.selectedAsignaturas
            .map((id) => this.asignaturas.find((a) => a.id_asignatura === id)?.glosa || id)
            .join(', ')
        : 'Todas';

      const data = [
        ['Informe de Interacción de Juegos - Resumen por Asignatura'],
        [''],
        ['Colegio:', colegio],
        ['Cursos:', cursos],
        ['Asignaturas:', asignaturas],
        ['Fecha Inicio:', this.inicio],
        ['Fecha Fin:', this.fin],
        [''],
        ['Asignatura', 'Correctas', 'Incorrectas'],
        ...this.resumenJuegos.map((item) => [
          item.nombre_asignatura || '',
          item.correctas,
          item.incorrectas,
        ]),
        [''],
        ['Detalle de Interacciones de Juegos'],
        ['ID Juego', 'ID Alumno', 'RUT Alumno', 'Asignatura', 'Curso', 'Colegio', 'Nombre Alumno', 'Capítulo', 'Video', 'Pregunta', 'Respuesta', 'Fecha Inicio', 'Fecha Fin', 'Puntaje Obtenido'],
        ...this.informeJuegos.map((item) => [
          item.id_juego,
          item.id_alumno,
          item.rut_alumno,
          item.asignatura,
          item.curso,
          item.colegio,
          item.nombre_alumno,
          item.capitulo,
          item.video,
          item.glosa_pregunta,
          item.respuesta_juego,
          new Date(item.fecha_inicio_juego).toLocaleString('es-CL'),
          new Date(item.fecha_fin_juego).toLocaleString('es-CL'),
          item.puntaje_obtenido,
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(data);

      worksheet['!cols'] = [
        { wch: 15 }, // ID Juego
        { wch: 15 }, // ID Alumno
        { wch: 15 }, // RUT Alumno
        { wch: 30 }, // Asignatura
        { wch: 20 }, // Curso
        { wch: 30 }, // Colegio
        { wch: 30 }, // Nombre Alumno
        { wch: 30 }, // Capítulo
        { wch: 30 }, // Video
        { wch: 40 }, // Pregunta
        { wch: 15 }, // Respuesta
        { wch: 20 }, // Fecha Inicio
        { wch: 20 }, // Fecha Fin
        { wch: 15 }, // Puntaje Obtenido
      ];
      worksheet['!rows'] = [
        { hpt: 30 }, // Title row
        {}, // Empty row
        {}, // Colegio
        {}, // Cursos
        {}, // Asignaturas
        {}, // Fecha Inicio
        {}, // Fecha Fin
        {}, // Empty row
        { hpt: 20 }, // Header row (Resumen)
        ...this.resumenJuegos.map(() => ({})),
        {}, // Empty row
        { hpt: 20 }, // Header row (Detalle)
      ];

      worksheet['A1'] = {
        ...worksheet['A1'],
        s: {
          font: { bold: true, sz: 16 },
          alignment: { horizontal: 'center', vertical: 'center' },
        },
      };

      ['A9', 'B9', 'C9'].forEach((cell) => {
        worksheet[cell] = {
          ...worksheet[cell],
          s: {
            font: { bold: true },
            fill: { fgColor: { rgb: 'D3D3D3' } },
            border: {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            },
            alignment: { horizontal: 'center', vertical: 'center' },
          },
        };
      });

      const detalleHeaderRow = 10 + this.resumenJuegos.length + 1;
      ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'].forEach((col) => {
        const cell = `${col}${detalleHeaderRow}`;
        worksheet[cell] = {
          ...worksheet[cell],
          s: {
            font: { bold: true },
            fill: { fgColor: { rgb: 'D3D3D3' } },
            border: {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            },
            alignment: { horizontal: 'center', vertical: 'center' },
          },
        };
      });

      for (let row = detalleHeaderRow + 1; row < detalleHeaderRow + 1 + this.informeJuegos.length; row++) {
        ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'].forEach((col) => {
          const cell = `${col}${row}`;
          worksheet[cell] = {
            ...worksheet[cell],
            s: {
              border: {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' },
              },
              alignment: { horizontal: col === 'C' || col === 'D' || col === 'F' || col === 'G' || col === 'I' || col === 'J' ? 'left' : 'center', vertical: 'center' },
            },
          };
        });
      }

      ['A3', 'A4', 'A5', 'A6', 'A7'].forEach((cell, index) => {
        worksheet[cell] = {
          ...worksheet[cell],
          s: {
            font: { bold: true },
            alignment: { horizontal: 'left', vertical: 'center' },
          },
        };
        const valueCell = `B${3 + index}`;
        worksheet[valueCell] = {
          v: data[2 + index][1],
          s: {
            alignment: { horizontal: 'left', vertical: 'center' },
          },
        };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ResumenJuegos');
      XLSX.writeFile(workbook, `Resumen_Juegos_${new Date().toISOString().split('T')[0]}.xlsx`, {
        bookType: 'xlsx',
        type: 'array',
      });

      this.$toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Resumen exportado a Excel correctamente.',
        life: 3000,
      });
    },
    onCursoChange(newValue) {
      this.selectedCursos = newValue || [];
      this.errors.cursos = '';
    },
    onAsignaturaChange(newValue) {
      this.selectedAsignaturas = newValue || [];
      this.errors.asignaturas = '';
    },
    onPageJuegos(event) {
      this.currentPageJuegos = event.page + 1;
      this.rows = event.rows;
      this.generarInformeJuegos();
    },
  },
  mounted() {
    this.loadColegios();
  },
};
</script>

<style scoped>
.informe-uso-container {
  @apply p-4;
}

.informe-uso-card {
  @apply bg-white rounded-lg shadow p-4;
}

.card-header {
  @apply flex justify-between items-center mb-4;
}

.card-title {
  @apply text-xl font-bold text-gray-800;
}

.form-field {
  @apply mb-4;
}

.form-field label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}

.p-inputtext {
  @apply border-gray-300 rounded-md shadow-sm;
}

:deep(.p-datatable-sm) .p-datatable-thead > tr > th,
:deep(.p-datatable-sm) .p-datatable-tbody > tr > td {
  @apply p-2 text-sm;
}

.flex.justify-end {
  display: flex;
  justify-content: flex-end;
}
</style>
